<template>
  <TeamBadgeButton
    v-if="useTeamBadges"
    :class="[classes.btnBase]"
    :isLeft="isLeft"
    :isDraw="isDraw"
    :option="option"
    :competitor="competitor"
    data-testid="parlay-team-badge-button"
    :isSelected="isSelected"
    :homeOrAwayTeam="homeOrAwayTeam"
    :eventId="eventId"
    @click="handleClick"
  />
  <TeamTextButton
    v-else
    :class="[classes.btnBase]"
    :option="option"
    :isDraw="isDraw"
    data-testid="parlay-text-button"
    :isSelected="isSelected"
    :competitor="competitor"
    @click="handleClick"
  />
</template>

<script lang="ts">
import { composeBetOption } from "@fresh8/ad-data-composition/src/betData";
import { createUseStyles } from "@fresh8/vue-jss/src";
import { computed, defineComponent, PropType } from "vue";
import TeamBadgeButton from "./TeamBadgeButton.vue";
import TeamTextButton from "./TeamTextButton.vue";
import { injectParlayStates } from "@fresh8/ad-data-composition/src/parlayStates";
import { useHandleAnalytics } from "../../../lib/analytics";
import { useHandleCardAnalytics } from "../../../lib/card-analytics";
import { AnalyticEvents } from "@fresh8/types/src/analytics";
import { SportCompetitor } from "@fresh8/types/src/betData";
import { injectTeamColors } from "../../..//lib/teamBranding";
import { injectCardNumber } from "../../../hooks/useAnalytics";

const useStyles = createUseStyles({
  btnBase: {
    textTransform: "uppercase",
    flex: "1",
    textAlign: "center",

    "&:hover": {
      cursor: "pointer"
    }
  }
});

export default defineComponent({
  name: "ParlayButton",
  components: {
    TeamBadgeButton,
    TeamTextButton
  },
  props: {
    eventId: { type: String, required: true },
    marketId: { type: String, required: true },
    optionId: { type: String, required: true },
    isLeft: { type: Boolean, required: false, default: false },
    isDraw: { type: Boolean, required: false },
    competitor: {
      type: Object as PropType<SportCompetitor>,
      required: false,
      default: undefined
    },
    disableBadges: {
      type: Boolean,
      required: false
    },
    homeOrAwayTeam: {
      type: String as PropType<"home" | "away">,
      default: "home",
      required: true
    }
  },
  setup(props) {
    const handleAnalytics = useHandleAnalytics();
    const handleCardAnalytics = useHandleCardAnalytics();
    const option = composeBetOption(props.optionId);
    const parlayContext = injectParlayStates();
    const cardNumber = injectCardNumber();

    const teamColorsContext = injectTeamColors();
    const isButtonSelected = computed(
      () =>
        parlayContext?.findSelection({
          eventId: props.eventId,
          marketId: props.marketId,
          optionId: props.optionId
        }) !== -1
    );

    const handleClick = (event: MouseEvent) => {
      if (option?.value?.suspended) {
        return;
      }
      parlayContext?.updateSelections({
        eventId: props.eventId,
        marketId: props.marketId,
        optionId: props.optionId
      });
      handleAnalytics(AnalyticEvents.INTERACTED, "Parlay Selection");
      handleCardAnalytics(
        AnalyticEvents.INTERACTED,
        "Parlay Selection",
        undefined,
        cardNumber,
        option.value?.name.full
      );
      event.preventDefault();
      event.stopPropagation();
    };

    const useTeamBadges = computed(
      () => teamColorsContext.teamIconsAvailable.value
    );

    return {
      useTeamBadges,
      option,
      classes: useStyles(),
      handleClick,
      isSelected: isButtonSelected
    };
  }
});
</script>
